import React, { useEffect, useState, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import SaveIcon from "@mui/icons-material/Save";
import TimerIcon from "@mui/icons-material/Timer";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
// Simplified outcomes - only the two outcomes you requested
const CALL_OUTCOMES = [
  { value: "successful", label: "Successful" },
  { value: "not_interested", label: "Not Interested" },
  { value: "missed", label: "Missed" },
  { value: "follow_up_needed", label: "Follow-up Needed" },
  { value: "callback_requested", label: "Callback Requested" },
];

export default function CallInterface({ contact, open, onClose, onCallSaved }) {
  const [callState, setCallState] = useState("idle"); // idle | dialing | active | ended
  const [socketConnected, setSocketConnected] = useState(false);
  const [reminderNotes, setReminderNotes] = useState("");
  const socketRef = useRef(null);

  const getAllowedOutcomes = (status) => {
    const s = (status || "").toString().toLowerCase();
    if (s === "connected") {
      return [
        "successful",
        "follow_up_needed",
        "callback_requested",
        "not_interested",
      ];
    }
    return ["missed", "follow_up_needed"];
  };

  const [callData, setCallData] = useState({
    id: null,
    contact: contact?.id || "",
    direction: "outbound",
    phone_number: "",
    status: "in_progress",
    duration: "00:00:00",
    start_time: "",
    end_time: "",
    notes: "",
    outcome: "successful",
    call_uuid: "",
    tags: [],
    follow_up_date: "",
  });

  const [callTimer, setCallTimer] = useState(0);
  const [selectedPhoneIndex, setSelectedPhoneIndex] = useState(0);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [followUpDateTime, setFollowUpDateTime] = useState("");
  const [reminderActivityTypeId, setReminderActivityTypeId] = useState(null);
  const [defaultActivityTypeId, setDefaultActivityTypeId] = useState("");

  const { domain, token, userDetails } = useSelector((s) => s.tokenReducer);
  const getLocalDateTimeString = (date = new Date()) => {
    const offsetMs = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offsetMs);
    return localDate.toISOString().slice(0, 16);
  };
  // restore active socket if exists
  useEffect(() => {
    const activeUuid = localStorage.getItem("active_call_uuid");
    if (activeUuid) {
      console.log("Restoring WebSocket for active call:", activeUuid);
      initWebSocket(activeUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const allowed = getAllowedOutcomes(callData.status);
    if (!allowed.includes(callData.outcome)) {
      const newOutcome = allowed[0];
      setCallData((prev) => ({ ...prev, outcome: newOutcome }));
      setShowFollowUpForm(
        ["callback_requested", "follow_up_needed"].includes(newOutcome)
      );
    }
  }, [callData.outcome, callData.status]);

  // Timer management
  useEffect(() => {
    let interval;
    if (callState === "active") {
      interval = setInterval(() => setCallTimer((prev) => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [callState]);

  // select primary phone
  useEffect(() => {
    if (contact?.phone_numbers?.length > 0) {
      const primary =
        contact.phone_numbers.find((p) => p.primary) ||
        contact.phone_numbers[0];
      const index = contact.phone_numbers.findIndex((p) => p === primary);
      setSelectedPhoneIndex(index);
      setCallData((prev) => ({
        ...prev,
        phone_number: primary.number,
        contact: contact.id,
      }));
    }
  }, [contact]);

  const handlePhoneSelection = (index) => {
    setSelectedPhoneIndex(index);
    if (contact?.phone_numbers?.[index]) {
      setCallData((prev) => ({
        ...prev,
        phone_number: contact.phone_numbers[index].number,
      }));
    }
  };

  // --- Start call (initiate telephony then create crm call) ---
  const startCall = async () => {
    try {
      setCallState("dialing");

      const headers = { Authorization: `Token ${token}` };

      // Step 1: initiate telephony
      const telephonyRes = await axios.post(
        `${domain}crm/make/calls/`,
        {
          user_id: userDetails?.id || "",
          customer_number: callData.phone_number,
        },
        { headers }
      );

      const call_uuid = telephonyRes?.data?.success?.call_id;
      if (!call_uuid) {
        throw new Error("No call_uuid received from telephony provider");
      }

      // Step 2: create CRM call record
      const crmPayload = {
        contact: contact.id,
        phone_number: callData.phone_number,
        direction: "outbound",
        call_uuid,
        start_time: new Date().toISOString(),
        status: "in_progress",
        assigned_to: userDetails?.id,
      };

      const crmRes = await axios.post(`${domain}crm/calls/`, crmPayload, {
        headers,
      });

      setCallData((prev) => ({
        ...prev,
        id: crmRes.data.id,
        call_uuid,
        start_time: crmRes.data.start_time || crmPayload.start_time,
      }));

      localStorage.setItem("active_call_uuid", call_uuid);

      // open websocket and prevent close once websocket connects (requirement)
      initWebSocket(call_uuid);

      toast.success("Call initiated and logged");
    } catch (err) {
      console.error("Error starting call:", err);
      toast.error(
        err?.response?.data?.detail || err.message || "Failed to initiate call"
      );
      setCallState("idle");
    }
  };

  // --- WebSocket initialization ---
  const initWebSocket = (uuid) => {
    if (!uuid) return;

    try {
      // close any existing
      if (socketRef.current) {
        socketRef.current.close();
      }
      console.log(window.location.host);
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(
        `${protocol}://${window.location.host}/ws/call/?call_uuid=${uuid}`
      );

      socketRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket open for", uuid);
        setSocketConnected(true); // prevent dialog close
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Typical events: call_connected, call_ended, call_update
          if (data.event === "call_connected") {
            setCallState("active");
            setCallTimer(0);
          }

          if (data.event === "call_ended") {
            handleBackendEndCall(data);
          }

          // generic update (duration, agent_status etc.)
          if (data.event === "call_update") {
            setCallData((prev) => ({ ...prev, ...data.payload }));
          }
        } catch (e) {
          console.error("Invalid WS message", e);
        }
      };

      ws.onerror = (err) => {
        console.error("WebSocket error", err);
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        setSocketConnected(false);
      };

      // keep reference on window for debugging/cleanup
      window.activeCallSocket = ws;
    } catch (err) {
      console.error("Failed to init websocket", err);
    }
  };

  const handleBackendEndCall = (data) => {
    console.log("Handling backend call end:", data);
    setCallState("ended");

    setCallData((prev) => ({
      ...prev,
      status: data.status || prev.status,
      agent_status: data.agent_status || prev.agent_status,
      customer_status: data.customer_status || prev.customer_status,
      duration: data.duration ?? prev.duration,
      end_time: new Date().toISOString(),
    }));

    localStorage.removeItem("active_call_uuid");

    if (socketRef.current) {
      try {
        socketRef.current.close();
      } catch (e) {
        /* ignore */
      }
      socketRef.current = null;
    }

    setSocketConnected(false);
    toast.info("Call ended by backend");
  };

  // --- Outcome handling (simplified) ---
  const handleOutcomeChange = (e) => {
    const value = e.target.value;
    const allowed = getAllowedOutcomes(callData.status);
    if (!allowed.includes(value)) {
      return;
    }
    const shouldShowFollowUp =
      value === "callback_requested" || value === "follow_up_needed";

    setCallData((prev) => ({ ...prev, outcome: value }));
    setShowFollowUpForm(shouldShowFollowUp);

    if (shouldShowFollowUp) {
      const tmr = new Date();
      tmr.setDate(tmr.getDate() + 1);
      tmr.setHours(10, 0, 0, 0);
      const dateStr = tmr.toISOString().slice(0, 16);
      setFollowUpDateTime(dateStr);
      setCallData((prev) => ({ ...prev, follow_up_date: dateStr }));
    } else {
      setFollowUpDateTime("");
      setCallData((prev) => ({ ...prev, follow_up_date: null }));
    }
  };

  useEffect(() => {
    const fetchActivityTypeOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/activity-type/`);
        const types = res.responseDetails || [];

        if (types.length > 0) {
          const noteType = types.find((t) => t.label?.toLowerCase() === "call");
          const defaultTypeId = noteType ? noteType.id : types[0].id;
          setDefaultActivityTypeId(defaultTypeId);

          const reminderType = types.find(
            (t) => t.label?.toLowerCase() === "call"
          );
          setReminderActivityTypeId(reminderType?.id || null);
        }
      } catch (err) {
        console.error("Failed to fetch contact status:", err);
      }
    };

    fetchActivityTypeOptions();
  }, [domain]);

  useEffect(() => {
    const fetchReminderTypes = async () => {
      const res = await customGetAPI(`${domain}crm/reminder-type/`);
      const reminders = res.responseDetails || [];
      if (reminders.length > 0) {
        const reminderType = reminders.find(
          (t) => t.label?.toLowerCase() === "call"
        );
        setReminderActivityTypeId(reminderType?.id || null);
      }
    };
    fetchReminderTypes();
  }, [domain]);

  // --- Save final call (PUT) ---
  const saveCall = async () => {
    try {
      const headers = { Authorization: `Token ${token}` };

      // include follow_up_date and notes in the payload so backend can create reminder
      const payload = {
        ...callData,
        follow_up_date: callData.follow_up_date || null,
        notes: callData.notes || "",
      };

      await axios.put(`${domain}crm/calls/${callData.id}/`, payload, {
        headers,
      });

      await axios.post(
        `${domain}crm/activities/`,
        {
          contact: contact.id,
          type: defaultActivityTypeId,
          title: `${
            callData.direction === "outbound" ? "outbound" : "inbound"
          } call - ${callData.status}`,
          description:
            callData.notes ||
            // `Call ${callData.status} after ${Math.floor(
            //   callData.duration / 60
            // )}:${(callData.duration % 60).toString().padStart(2, "0")}`,
            `Call ${callData.status} after call duration of ${callData.duration}`,
          date: getLocalDateTimeString(),
          completed: callData.status.toLowerCase() === "connected",
        },
        {
          headers: { Authorization: `token ${token}` },
        }
      );

      if (
        ["callback_requested", "follow_up_needed"].includes(callData.outcome) &&
        callData.follow_up_date
      ) {
        await axios.post(
          `${domain}crm/reminders/`,
          {
            contact: contact.id,
            title: `${
              callData.outcome === "callback_requested"
                ? "Callback"
                : "Follow-up"
            } - ${contact.first_name} ${contact.last_name}`,
            notes: `Based on call on ${format(
              new Date(callData.start_time),
              "MMM d"
            )}. Call Notes: ${callData.notes || "N/A"} | Reminder Notes: ${
              reminderNotes || "N/A"
            }`,
            date: callData.follow_up_date,
            priority: "high",
            type: reminderActivityTypeId,
            assigned_to: userDetails?.id,
          },
          {
            headers: { Authorization: `token ${token}` },
          }
        );
      }

      toast.success("Call saved successfully");

      // cleanup
      localStorage.removeItem("active_call_uuid");
      if (socketRef.current) socketRef.current.close();
      setSocketConnected(false);

      if (onCallSaved) onCallSaved();
      onClose();
    } catch (err) {
      console.error("Failed to save call", err);
      toast.error(err?.response?.data || "Error saving call");
    }
  };

  const formatTimer = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, "0");
    const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  };

  const canClose =
    socketConnected === false &&
    callState !== "active" &&
    callState !== "dialing";

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={!canClose}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PhoneIcon color="primary" />
            <Typography variant="subtitle1">
              Call — {contact?.first_name} {contact?.last_name}
            </Typography>
          </Stack>

          <IconButton onClick={onClose} size="small" disabled={!canClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={
              <Typography variant="h6" color="primary">
                Contact Information
              </Typography>
            }
          />
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
                {contact?.first_name?.[0]}
                {contact?.last_name?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {contact?.first_name} {contact?.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {contact?.company}
                </Typography>
              </Box>
            </Stack>

            {contact?.phone_numbers?.length > 1 && (
              <Box sx={{ mt: 2 }}>
                <InputLabel sx={{ mb: 1 }}>Phone Number</InputLabel>
                <Select
                  disabled={callState !== "idle"}
                  value={selectedPhoneIndex}
                  onChange={(e) => handlePhoneSelection(e.target.value)}
                  fullWidth
                >
                  {contact.phone_numbers.map((phone, index) => (
                    <MenuItem key={index} value={index}>
                      {phone.number}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "grey.100",
                textAlign: "center",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle1">
                {callData.phone_number}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography>Call Controls</Typography>
                {callState === "active" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TimerIcon />
                    <Typography>{formatTimer(callTimer)}</Typography>
                  </Stack>
                )}
              </Box>
            }
          />

          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              {callState === "idle" && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PhoneIcon />}
                  onClick={startCall}
                >
                  Start Call
                </Button>
              )}

              {callState === "dialing" && (
                <Box sx={{ textAlign: "center", my: 2 }}>
                  {/* GIF if you have one placed in /public/assets, otherwise circular progress fallback */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src="/assets/call-animation.gif"
                      alt="Dialing..."
                      style={{ width: 140, height: 140 }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <CircularProgress size={56} sx={{ position: "absolute" }} />
                  </Box>

                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Connecting your call...
                  </Typography>
                </Box>
              )}
            </Box>

            {(callState === "active" || callState === "dialing") && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2">Call Notes</Typography>
                <TextField
                  placeholder="Type notes..."
                  multiline
                  rows={3}
                  fullWidth
                  value={callData.notes}
                  onChange={(e) =>
                    setCallData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {callState === "ended" && (
          <Card sx={{ mt: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader title="Call Summary" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography>
                    <strong>Start:</strong>{" "}
                    {callData.start_time
                      ? format(new Date(callData.start_time), "p")
                      : "--"}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography>
                    <strong>End:</strong>{" "}
                    {callData.end_time
                      ? format(new Date(callData.end_time), "p")
                      : "--"}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography>
                    <strong>Duration:</strong> {formatTimer(callTimer)}
                  </Typography>
                </Grid>
              </Grid>
              {/* --- Call Notes Section --- */}
              <Box sx={{ mt: 3 }}>
                <TextField
                  placeholder="Add notes about this call..."
                  label="Call Notes"
                  multiline
                  rows={3}
                  fullWidth
                  value={callData.notes}
                  onChange={(e) =>
                    setCallData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </Box>
              {/* Outcomes - only follow up & callback */}
              <Box sx={{ mt: 2 }}>
                <InputLabel>Call Outcome</InputLabel>
                <Select
                  value={callData.outcome}
                  onChange={handleOutcomeChange}
                  fullWidth
                >
                  {CALL_OUTCOMES.map((o) => {
                    const isConnected =
                      (callData.status || "").toLowerCase() === "connected";
                    const disableMissed = isConnected && o.value === "missed";
                    const disableSuccessOrCallback =
                      !isConnected &&
                      [
                        "successful",
                        "callback_requested",
                        "not_interested",
                      ].includes(o.value);

                    return (
                      <MenuItem
                        key={o.value}
                        value={o.value}
                        disabled={disableMissed || disableSuccessOrCallback}
                      >
                        {o.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
              {showFollowUpForm && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CalendarIcon />
                    {callData.outcome === "callback_requested"
                      ? "Callback Date & Time"
                      : "Follow-up Date & Time"}
                  </Typography>

                  <TextField
                    type="datetime-local"
                    fullWidth
                    value={followUpDateTime}
                    onChange={(e) => {
                      setFollowUpDateTime(e.target.value);
                      setCallData((prev) => ({
                        ...prev,
                        follow_up_date: e.target.value,
                      }));
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      borderRadius: 1,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFF8E1", // light yellow background
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    A reminder will be created for this date.
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="Reminder Notes"
                      placeholder="Add specific notes for the reminder..."
                      fullWidth
                      multiline
                      rows={3}
                      value={reminderNotes}
                      onChange={(e) => setReminderNotes(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#F3F8FF", // light blue background
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={saveCall}
                >
                  Update
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
}
